import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangleIcon, ArrowLeftIcon, CalendarDaysIcon, MailIcon, MapPinIcon, PencilIcon, PhoneIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { Campo, EncabezadoPanel, EstadoVacio, Panel } from '../components/ui/Basicos';
import { Avatar, ChipEstado, ChipMateria } from '../components/ui/Etiquetas';
import { ChipVencimiento } from '../components/ui/Semaforo';
import { EditorAbogado } from '../components/abogados/EditorAbogado';
import { PanelGoogleCalendar } from '../components/google/PanelGoogleCalendar';
import { useDatos } from '../contexts/DatosContext';
import { inicialesDe } from '../data/abogados';
import { fechaCorta, fechaLarga } from '../utils/fechas';
import { proximoPlazo } from '../utils/consultas';
import { cargaDe, LIMITE_CRITICOS, LIMITE_EXPEDIENTES } from '../utils/personal';
export function AbogadoDetalle() {
  const {
    id = ''
  } = useParams();
  const {
    abogados,
    expedientes,
    plazos,
    audiencias,
    sucursales,
    rol,
    etapasDe,
    actualizarAbogado,
    usuarioActualId
  } = useDatos();
  const [editando, setEditando] = useState(false);
  const abogado = abogados.find(a => a.id === id);
  const carga = useMemo(() => abogado ? cargaDe(abogado, expedientes, plazos, audiencias) : null, [abogado, expedientes, plazos, audiencias]);
  if (!abogado || !carga) {
    return <Panel data-id="element-932" data-mp="fl2oh6q9btka">
        <EstadoVacio mensaje="No encontramos a esa persona" sugerencia="Vuelve al listado de abogados y personal." data-id="element-933" data-mp="fkj3ti982r2k" />
      </Panel>;
  }
  const sucursal = sucursales.find(s => s.id === abogado.sucursalId);
  return <>
      <EncabezadoPagina titulo={abogado.nombre} descripcion={`${abogado.rol} · ${sucursal?.nombre ?? 'Sin sucursal'} · ingresó el ${fechaLarga(abogado.fechaIngreso)}`} acciones={<>
            <Link to="/abogados" className="inline-flex h-9 items-center gap-2 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-935" data-mp="2uep2e97lw978">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-936" data-mp="346cri3goxe5j" />
              Personal
            </Link>
            {rol === 'Administrador' && <button type="button" onClick={() => setEditando(v => !v)} className="inline-flex h-9 items-center gap-2 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-marino-700 transition-colors duration-150 hover:border-marino-300 hover:bg-marino-50" data-id="element-937" data-mp="3d4x02bsje7g1">
                <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-938" data-mp="3i52l394awvp5" />
                Editar
              </button>}
          </>} data-id="element-934" data-mp="ofgzuy7o2jy4" />

      {carga.sobrecargado && <p className="mb-4 flex items-start gap-2 rounded border border-peligro-100 bg-peligro-50 px-4 py-2.5 text-xs text-peligro-700" data-id="element-939" data-mp="1nrti8069g8fv">
          <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-940" data-mp="3atu9j05ahto" />
          <span data-id="element-941" data-mp="89n7dabis6tb">
            <span className="font-semibold" data-id="element-942" data-mp="3po243dnuol7z">Señal de sobrecarga.</span> Tiene {carga.activos}
            expedientes activos {carga.activos > LIMITE_EXPEDIENTES && `(límite ${LIMITE_EXPEDIENTES})`}{' '}
            y {carga.criticos} términos críticos{' '}
            {carga.criticos >= LIMITE_CRITICOS && `(umbral ${LIMITE_CRITICOS})`}. Considera
            reasignar algunos vencimientos.
          </span>
        </p>}

      {!abogado.activo && <p className="mb-4 rounded border border-gris-200 bg-gris-100 px-4 py-2.5 text-xs text-gris-600" data-id="element-943" data-mp="36qr57u64rtt2">
          Persona inactiva: conserva su historial pero no aparece como opción al asignar nuevos
          expedientes.
        </p>}

      <Panel data-id="element-944" data-mp="19wxp2yyhlh37">
        {editando && rol === 'Administrador' ? <EditorAbogado abogado={abogado} esNuevo={false} onGuardar={a => {
        actualizarAbogado(a);
        setEditando(false);
      }} onCancelar={() => setEditando(false)} data-id="element-945" data-mp="19w4surx5m9zb" /> : <div className="flex flex-col gap-5 px-5 py-5 lg:flex-row" data-id="element-946" data-mp="2wyba17j8fmib">
            <Avatar iniciales={inicialesDe(abogado.nombre)} foto={abogado.foto} tamano="lg" nombre={abogado.nombre} tono={abogado.activo ? 'marino' : 'gris'} data-id="element-947" data-mp="2s0t1hi94n9au" />
            <dl className="grid flex-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-948" data-mp="2i8x3s8aydl75">
              <Campo etiqueta="Cédula profesional" valor={abogado.cedula} mono data-id="element-949" data-mp="2e9o8v046fha0" />
              <Campo etiqueta="Rol en el sistema" valor={abogado.rol} data-id="element-950" data-mp="29al4lhw0tf33" />
              <Campo etiqueta="Materias" valor={abogado.especialidades.length > 0 ? <span className="flex flex-wrap gap-1.5" data-id="element-952" data-mp="1zcx7ilaxzvhr">
                      {abogado.especialidades.map(m => <ChipMateria key={m} materia={m} data-id="element-953" data-mp="1p7fi5rw3hzyc" />)}
                    </span> : 'Sin materias'} data-id="element-951" data-mp="24ap803pnkpxk" />
              <Campo etiqueta="Estado" valor={abogado.activo ? 'Activo' : 'Inactivo'} data-id="element-954" data-mp="1z40n6d847b26" />
              <Campo etiqueta="Correo" valor={<span className="inline-flex items-center gap-1.5" data-id="element-956" data-mp="16ir1ji4nagsd">
                    <MailIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-957" data-mp="1bfhh84cjfad9" />
                    {abogado.correo}
                  </span>} data-id="element-955" data-mp="242vey4o289yf" />
              <Campo etiqueta="Teléfono" valor={<span className="inline-flex items-center gap-1.5" data-id="element-959" data-mp="nqpqziiid234">
                    <PhoneIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-960" data-mp="nqg3c1kxr70l" />
                    {abogado.telefono || '—'}
                  </span>} data-id="element-958" data-mp="1l5tztr0sktgr" />
              <Campo etiqueta="Sucursal" valor={sucursal?.nombre ?? '—'} data-id="element-961" data-mp="kniih73ld3ix" />
              <Campo etiqueta="Fecha de ingreso" valor={fechaCorta(abogado.fechaIngreso)} data-id="element-962" data-mp="foyznfkao7us" />
            </dl>
          </div>}
      </Panel>

      <div className="mt-4" data-id="element-963" data-mp="ugr24qyzprf3">
        <PanelGoogleCalendar abogado={abogado} puedeEditar={rol === 'Administrador' || abogado.id === usuarioActualId} data-id="element-964" data-mp="pfa2szw5md0m" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4" data-id="element-965" data-mp="1sbq8db5np7z">
        {[{
        etiqueta: 'Expedientes activos',
        valor: carga.activos,
        alerta: carga.sobrecargado
      }, {
        etiqueta: 'Expedientes totales',
        valor: carga.expedientes.length,
        alerta: false
      }, {
        etiqueta: 'Términos vigentes',
        valor: carga.plazos.length,
        alerta: false
      }, {
        etiqueta: 'Términos críticos',
        valor: carga.criticos,
        alerta: carga.criticos > 0
      }].map(m => <Panel key={m.etiqueta} className="px-5 py-4" data-id="element-966" data-mp="1i3z5h3cuyqeh">
            <p className="text-2xs uppercase tracking-wide text-gris-400" data-id="element-967" data-mp="1pngsiqpb9yfs">{m.etiqueta}</p>
            <p className={`tabular mt-1 text-2xl font-semibold ${m.alerta ? 'text-peligro-600' : 'text-marino-900'}`} data-id="element-968" data-mp="1umthpi3nnhlj">
              {m.valor}
            </p>
          </Panel>)}
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[1.6fr_1fr]" data-id="element-969" data-mp="1kp6zaln5d7wt">
        <Panel data-id="element-970" data-mp="28fj2jj7g6gh2">
          <EncabezadoPanel titulo="Expedientes asignados" descripcion="Etapa actual y próximo vencimiento de cada asunto a su cargo." data-id="element-971" data-mp="28fsx7k3ftbt3" />
          {carga.expedientes.length === 0 ? <EstadoVacio mensaje="Sin expedientes asignados" data-id="element-972" data-mp="1zlxpq5euf9aa" /> : <div className="overflow-x-auto scroll-fino" data-id="element-973" data-mp="23mggrx8wwbbf">
              <table className="w-full min-w-[720px] text-sm" data-id="element-974" data-mp="2rdlwmceur3lq">
                <thead data-id="element-975" data-mp="2rb95b8n7fgw0">
                  <tr className="bg-gris-50 text-left text-2xs uppercase tracking-wide text-gris-500" data-id="element-976" data-mp="2w42dy7lsn9dd">
                    <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-977" data-mp="27d64qrt035a1">
                      Localizador
                    </th>
                    <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-978" data-mp="22lobkpx7y8zm">
                      Cliente
                    </th>
                    <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-979" data-mp="1xnwezilmpyp3">
                      Etapa actual
                    </th>
                    <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-980" data-mp="2qc0hxwctfxo3">
                      Próximo vencimiento
                    </th>
                    <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-981" data-mp="2leigss51imqn">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gris-100" data-id="element-982" data-mp="2bft0h3sslaci">
                  {carga.expedientes.map(e => {
                const etapas = etapasDe(e.materia);
                const etapa = etapas[Math.min(e.etapaActual, etapas.length - 1)];
                const plazo = proximoPlazo(plazos, e.id);
                return <tr key={e.id} className="align-top hover:bg-gris-50" data-id="element-983" data-mp="3bl4v895b7sk0">
                        <td className="px-5 py-3" data-id="element-984" data-mp="3gn2jjhskhdjt">
                          <Link to={`/expedientes/${e.id}`} className="font-mono text-xs font-semibold text-marino-700 hover:underline" data-id="element-985" data-mp="3llnofuirklel">
                            {e.localizador}
                          </Link>
                          <div className="mt-1" data-id="element-986" data-mp="374mxe18p9d2">
                            <ChipMateria materia={e.materia} data-id="element-987" data-mp="35tj5e7so1ly" />
                          </div>
                        </td>
                        <td className="max-w-[14rem] px-3 py-3 text-xs text-gris-700" data-id="element-988" data-mp="84ejddn1qlae">
                          {e.cliente}
                        </td>
                        <td className="px-3 py-3 text-xs text-gris-700" data-id="element-989" data-mp="29ynj3i5kf1qo">{etapa?.nombre ?? '—'}</td>
                        <td className="px-3 py-3" data-id="element-990" data-mp="2ew5pb0s772uz">
                          <ChipVencimiento fechaLimite={plazo?.fechaLimite} data-id="element-991" data-mp="2eys49lhf4kn8" />
                          {plazo && <p className="mt-0.5 max-w-[14rem] truncate text-2xs text-gris-400" data-id="element-992" data-mp="24udzvstnbg6t">
                              {plazo.concepto}
                            </p>}
                        </td>
                        <td className="px-5 py-3" data-id="element-993" data-mp="2dwdgik0r0i98">
                          <ChipEstado estado={e.estado} data-id="element-994" data-mp="2dszghifk4l4y" />
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </Panel>

        <Panel data-id="element-995" data-mp="2sjyk0f1jcjho">
          <EncabezadoPanel titulo="Agenda próxima" descripcion={`${carga.audiencias.length} audiencias y citas por delante`} data-id="element-996" data-mp="2sljclz4ahscw" />
          {carga.audiencias.length === 0 ? <EstadoVacio mensaje="Sin audiencias ni citas programadas" data-id="element-997" data-mp="31mhmgy1esu3e" /> : <ul className="divide-y divide-gris-100" data-id="element-998" data-mp="36krifauyjyq2">
              {carga.audiencias.slice(0, 8).map(a => {
            const exp = expedientes.find(e => e.id === a.expedienteId);
            return <li key={a.id} className="px-5 py-3" data-id="element-999" data-mp="pw8b7x140fb4">
                    <div className="flex items-start justify-between gap-3" data-id="element-1000" data-mp="lv6b7dzdbz83">
                      <p className="text-sm font-medium text-gris-800" data-id="element-1001" data-mp="gx4siu5uu1x5">{a.titulo}</p>
                      <span className={`shrink-0 rounded px-2 py-0.5 text-2xs font-medium ${a.tipo === 'Audiencia' ? 'bg-marino-800 text-white' : 'bg-gris-100 text-gris-600'}`} data-id="element-1002" data-mp="gz7tg98jpfgn">
                        {a.tipo}
                      </span>
                    </div>
                    <p className="tabular mt-1 inline-flex items-center gap-1.5 text-2xs text-gris-500" data-id="element-1003" data-mp="1xav0wd40bccl">
                      <CalendarDaysIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-1004" data-mp="1sjv0gxvoic5c" />
                      {fechaCorta(a.fecha)} · {a.hora}
                    </p>
                    <p className="mt-0.5 inline-flex items-center gap-1.5 text-2xs text-gris-500" data-id="element-1005" data-mp="2gfqbsxk95mx3">
                      <MapPinIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-1006" data-mp="22obhvvot41b9" />
                      {a.lugar}
                    </p>
                    {exp && <Link to={`/expedientes/${exp.id}`} className="mt-1 block font-mono text-2xs font-semibold text-marino-600 hover:underline" data-id="element-1007" data-mp="26ntzoilbzw9v">
                        {exp.localizador}
                      </Link>}
                  </li>;
          })}
            </ul>}
        </Panel>
      </div>
    </>;
}